import { create } from 'zustand';

/**
 * Rich manufacturer status for OEM Connect vehicles (locks, openings, tyres…),
 * mirrors the backend OemVehicleState. Rendered generically in the info panel.
 */
export interface OemVehicleState {
  centralLock?: 'locked' | 'unlocked' | 'partial';
  fields?: Record<string, string | number | boolean>;
}

export interface DevicePosition {
  lat: number;
  lng: number;
  speed: number;
  heading: number;
  altitude?: number;
  ignition?: boolean;
  movement?: boolean;
  satellites?: number;
  timestamp: string;
  receivedAt?: string;
  hdop?: number;
  // Power
  odometer?: number;
  batteryVoltage?: number;
  externalVoltage?: number;
  batteryLevel?: number;
  batteryCurrent?: number;
  // Fuel
  fuelLevel?: number;
  fuelConsumed?: number;
  fuelConsumption?: number;
  // OBD-II / Engine data
  engineRpm?: number;
  engineLoad?: number;
  coolantTemp?: number;
  throttlePosition?: number;
  obdSpeed?: number;
  // Network data
  gsmSignal?: number;
  cellId?: number;
  areaCode?: number;
  // Driver behavior
  harshAcceleration?: boolean;
  harshBraking?: boolean;
  harshCornering?: boolean;
  // Diagnostics
  dtcCount?: number;
  alarm?: string;
  // Driver
  driverId?: string;
  // EV (Electric Vehicle)
  evCharging?: boolean;
  evSoC?: number;
  evRange?: number;
  evSoH?: number;
  evBatteryTemp?: number;
  evHvCurrent?: number;
  evHvVoltage?: number;
  // Event
  eventId?: number;
  // Raw IO elements from tracker
  ioElements?: Record<string, unknown>;
  // OEM Connect: rich manufacturer status (locks, openings, tyres…)
  oem?: OemVehicleState;
}

export interface PositionWaypoint {
  lat: number;
  lng: number;
  speed: number;
  heading: number;
  timestamp: string;
  ignition?: boolean;
  hdop?: number;
}

// Track which device IMEIs have received new position data since last map render.
// Lives outside Zustand state to avoid triggering extra re-renders.
const changedDeviceImeis = new Set<string>();

/** Return the set of IMEIs that changed and clear it atomically. */
export function consumeChangedImeis(): Set<string> {
  const snapshot = new Set(changedDeviceImeis);
  changedDeviceImeis.clear();
  return snapshot;
}

// External position queue (outside Zustand state to avoid re-renders on every waypoint)
const positionQueues = new Map<string, PositionWaypoint[]>();

// Compression ratio per device (computed when batch arrives)
const queueCompressionRatios = new Map<string, number>();

/**
 * Compute time-compression ratio for a batch.
 * Goal: replay the batch in a reasonable animation time.
 *   batch < 10s real  → 1:1 (no compression)
 *   batch 10-60s      → 5x  (max 12s animation)
 *   batch 60-600s     → 60x (~10s animation)
 *   batch > 600s      → enough to cap at 15s animation
 */
function computeCompressionRatio(waypoints: PositionWaypoint[]): number {
  if (waypoints.length < 2) return 1;
  const firstTs = new Date(waypoints[0].timestamp).getTime();
  const lastTs = new Date(waypoints[waypoints.length - 1].timestamp).getTime();
  const spanMs = lastTs - firstTs;
  if (spanMs <= 0) return 1;

  const spanS = spanMs / 1000;
  if (spanS < 10) return 1;
  if (spanS < 60) return 5;
  if (spanS <= 600) return 60;
  // For very long batches, cap animation at 15s
  return spanS / 15;
}

export function getPositionQueue(imei: string): PositionWaypoint[] {
  return positionQueues.get(imei) || [];
}

export function getCompressionRatio(imei: string): number {
  return queueCompressionRatios.get(imei) || 1;
}

export function shiftPositionQueue(imei: string): PositionWaypoint | undefined {
  const queue = positionQueues.get(imei);
  if (!queue || queue.length === 0) return undefined;
  return queue.shift();
}

export function peekPositionQueue(imei: string): PositionWaypoint | undefined {
  const queue = positionQueues.get(imei);
  return queue && queue.length > 0 ? queue[0] : undefined;
}

export function getPositionQueueLength(imei: string): number {
  return positionQueues.get(imei)?.length || 0;
}

export function clearPositionQueue(imei: string): void {
  positionQueues.delete(imei);
  queueCompressionRatios.delete(imei);
}

export interface Device {
  id: string;
  imei: string;
  name: string;
  status: 'ONLINE' | 'OFFLINE' | 'UNKNOWN';
  plate?: string;
  brand?: string;
  model?: string;
  category?: string;
  trackerModel?: string;
  simIccid?: string | null;
  simType?: string | null;
  icon: string;
  markerColor: string;
  speedLimit?: number;
  vehicleType?: string;
  avgConsumption?: number;
  evEnabled?: boolean;
  evModelId?: string | null;
  evModel?: {
    id: string;
    make: string;
    model: string;
    variant: string | null;
    batteryKwh: number;
    rangeWltpKm: number;
    vehicleType: 'BEV' | 'PHEV';
  } | null;
  // Privacy mode: `privacyActive` is the live state (nothing is being recorded
  // right now), the rest is configuration. Absent `privacyOverride`, the window
  // comes from the tenant defaults. See backend/src/services/privacy.service.ts.
  privacyOverride?: boolean;
  privacyEnabled?: boolean;
  /** WORKING hours keyed by ISO weekday; privacy is their complement. */
  workSchedule?: Record<string, { allDay: true } | { start: string; end: string } | null> | null;
  privacyTheftOverride?: boolean;
  privacyActive?: boolean;
  lastPosition?: DevicePosition;
  lastPositionAt?: string;
  isConnected: boolean;
  group?: {
    id: string;
    name: string;
    color: string;
  };
}

interface DevicesState {
  devices: Device[];
  selectedDeviceId: string | null;
  isLoading: boolean;
  error: string | null;

  setDevices: (devices: Device[]) => void;
  addDevices: (devices: Device[]) => void;
  removeDevicesByImei: (imeis: string[]) => void;
  updateDevice: (imei: string, updates: Partial<Device>) => void;
  updatePosition: (imei: string, position: DevicePosition) => void;
  updatePositionBatch: (imei: string, waypoints: PositionWaypoint[], latestPosition: DevicePosition) => void;
  updateStatus: (imei: string, status: 'ONLINE' | 'OFFLINE') => void;
  selectDevice: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useDevicesStore = create<DevicesState>((set) => ({
  devices: [],
  selectedDeviceId: null,
  isLoading: false,
  error: null,

  setDevices: (devices) => set({ devices: Array.isArray(devices) ? devices : [], isLoading: false }),

  addDevices: (devices) =>
    set((state) => {
      const list = Array.isArray(state.devices) ? state.devices : [];
      const existingImeis = new Set(list.map((d) => d.imei));
      const newDevices = devices.filter((d) => !existingImeis.has(d.imei));
      return { devices: [...list, ...newDevices] };
    }),

  removeDevicesByImei: (imeis) =>
    set((state) => {
      const list = Array.isArray(state.devices) ? state.devices : [];
      const imeiSet = new Set(imeis);
      return { devices: list.filter((d) => !imeiSet.has(d.imei)) };
    }),

  updateDevice: (imei, updates) =>
    set((state) => {
      const list = Array.isArray(state.devices) ? state.devices : [];
      return {
        devices: list.map((d) =>
          d.imei === imei ? { ...d, ...updates } : d
        ),
      };
    }),

  updatePosition: (imei, position) => {
    changedDeviceImeis.add(imei);
    set((state) => {
      const list = Array.isArray(state.devices) ? state.devices : [];
      return {
        devices: list.map((d) =>
          d.imei === imei
            ? {
                ...d,
                lastPosition: position,
                lastPositionAt: position.receivedAt || position.timestamp,
                status: 'ONLINE' as const,
              }
            : d
        ),
      };
    });
  },

  updatePositionBatch: (imei, waypoints, latestPosition) => {
    changedDeviceImeis.add(imei);
    // Merge waypoints into the external queue (no downsampling — all positions matter,
    // especially heading-triggered ones during turns)
    const existing = positionQueues.get(imei) || [];
    const lastTs = existing.length > 0 ? existing[existing.length - 1].timestamp : '';

    // Only keep waypoints that are newer than what's already queued
    const newWaypoints = lastTs
      ? waypoints.filter((w) => w.timestamp > lastTs)
      : waypoints;

    const merged = [...existing, ...newWaypoints];
    positionQueues.set(imei, merged);

    // Compute compression ratio based on full batch time span
    queueCompressionRatios.set(imei, computeCompressionRatio(merged));

    // Update device lastPosition in Zustand state (for info panels)
    set((state) => {
      const list = Array.isArray(state.devices) ? state.devices : [];
      return {
        devices: list.map((d) =>
          d.imei === imei
            ? {
                ...d,
                lastPosition: latestPosition,
                lastPositionAt: latestPosition.receivedAt || latestPosition.timestamp,
                status: 'ONLINE' as const,
              }
            : d
        ),
      };
    });
  },

  updateStatus: (imei, status) =>
    set((state) => {
      const list = Array.isArray(state.devices) ? state.devices : [];
      return {
        devices: list.map((d) =>
          d.imei === imei ? { ...d, status } : d
        ),
      };
    }),

  selectDevice: (id) => set({ selectedDeviceId: id }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),
}));
